import { palette, spaces } from '@kimuno/theme';
import { Box, Dialog, DialogContent, List, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CancelIcon from '../../assets/subscription/cancel.svg';
import { cancellationReasons } from '../../constants';
import useScreenSize from '../../utils/useScreenSize';
import DialogActions from '../common/DialogActions';
import LabelledTextField from '../LabelledTextField/LabelledTextField';
import OptionItem from '../onboarding/OnboardingOptionItem';

// The API caps the whole composed reason at 2000 chars; the longest option label
// plus the "Anderer Grund: " prefix stays under 100, so this can't overflow it.
const FREE_TEXT_MAX_LENGTH = 1000;

interface StripeCancelDialogProps {
	open: boolean;
	isTrial: boolean;
	isLoading: boolean;
	onClose: () => void;
	onConfirm: (reason?: string) => void;
}

const StripeCancelDialog: React.FC<StripeCancelDialogProps> = (props) => {
	const { open, isTrial, isLoading, onClose, onConfirm } = props;
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const [option, setOption] = useState<string>('');
	const [freeText, setFreeText] = useState<string>('');

	// Reset the form so a previous choice doesn't linger on reopen.
	useEffect(() => {
		if (open) {
			setOption('');
			setFreeText('');
		}
	}, [open]);

	// The two inputs are mutually exclusive, so picking an option discards any
	// free text and vice versa — never both.
	const selectOption = (reason: string) => {
		setOption(reason);
		setFreeText('');
	};

	// Clear on the same trim test `buildReason` uses, so whitespace alone — which
	// would not count as a reason — can't silently drop a deliberate selection.
	const changeFreeText = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setFreeText(value);

		if (value.trim()) {
			setOption('');
		}
	};

	// Composed here rather than by the caller: the result is a localized sentence,
	// which can no longer be run through t() the way a bare option key could.
	const buildReason = (): string | undefined => {
		const typed = freeText.trim();

		if (typed) {
			return `${t('Other reason')}: ${typed}`;
		}

		return option ? t(option) : undefined;
	};

	return (
		<Dialog
			open={open}
			onClose={isLoading ? undefined : onClose}
			fullWidth
			maxWidth="lg"
			aria-labelledby="stripe-cancel-dialog-title"
		>
			<DialogContent sx={{ p: 0 }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: isXsScreen || isSmScreen ? 'column' : 'row',
						alignItems: 'flex-start',
						flex: '1 0 0',
						alignSelf: 'stretch',
					}}
				>
					{!isXsScreen && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								alignSelf: isSmScreen ? 'center' : 'flex-start',
								'& svg': {
									width: '400px',
									height: '300px',
								},
							}}
						>
							<CancelIcon />
						</Box>
					)}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							padding: spaces.lg,
							justifyContent: 'center',
							alignItems: 'flex-start',
							gap: spaces.lg,
							flex: '1 0 0',
						}}
					>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.xs }}>
							<Typography
								id="stripe-cancel-dialog-title"
								variant="h6"
								sx={{ color: palette.text.primary }}
							>
								{t('Too bad you want to leave!')}
							</Typography>
							<Typography variant="body2" sx={{ color: palette.text.secondary }}>
								{t(
									isTrial
										? 'You can enjoy your premium benefits until the end of your trial'
										: 'Once cancelled, your subscription will not renew. ' +
												'You keep full access until the end of your current billing period.'
								)}
							</Typography>
							<Typography variant="body2" sx={{ color: palette.text.secondary }}>
								{t(
									'Your feedback helps us make myLessons even better. What is the main reason for your resignation?'
								)}
							</Typography>
						</Box>
						<List
							dense
							sx={{
								padding: 0,
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: spaces.md,
							}}
						>
							{cancellationReasons.map((reason) => (
								<OptionItem
									key={reason}
									label={reason}
									isSelected={reason === option}
									isRadioButton={true}
									onClick={() => selectOption(reason)}
									sxProps={{ padding: spaces.md }}
								/>
							))}
						</List>
						<Box sx={{ width: '100%' }}>
							<LabelledTextField
								label={t('Other reason')}
								placeholder={t('Please briefly describe why you are leaving')}
								value={freeText}
								onChange={changeFreeText}
								maxLength={FREE_TEXT_MAX_LENGTH}
								dataTestId="cancel-reason-free-text"
							/>
						</Box>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions
				primaryButton={{
					text: t(isTrial ? 'Cancel Trial Subscription' : 'Cancel Subscription'),
					color: 'error',
					variant: 'contained',
					onClick: () => onConfirm(buildReason()),
					loading: isLoading,
				}}
				secondaryButton={{
					text: t('Keep subscription'),
					onClick: onClose,
					disabled: isLoading,
				}}
			/>
		</Dialog>
	);
};

export default StripeCancelDialog;
